import { Condition, ConditionType, Filters } from "@tenforce/product-fields"

import { FiltersDTO } from "../../models/dto/filters"
import { ConditionDTO, DefinitionDTOType } from "../../models/dto/conditions"

import TextConditionParser from "./TextConditionParser"
import DecimalConditionParser from "./DecimalConditionParser"
import BooleanConditionParser from "./BooleanConditionParser"
import {
    AccountCodeFieldDefinitionType,
    ActivityFieldDefinitionType,
    ApplicationFieldDefinitionType,
    ApplicationVersionFieldDefinitionType,
    BillTypeFieldDefinitionType,
    DisplayTypeFieldDefinitionType,
    ImpactFieldDefinitionType,
    ProbabilityFieldDefinitionType,
    OptionFieldDefinitionType,
    OptionsFieldDefinitionType,
    SelectorConditionParser,
    UserGroupFieldDefinitionType,
    UserGroupsFieldDefinitionType,
    UserFieldDefinitionType,
    UsersFieldDefinitionType,
    TagsFieldDefinitionType,
} from "./SelectorConditionParser"

export interface FilterParser {
    Type: ConditionType
    DefinitionDTOType: DefinitionDTOType
    checkIfApplicable: (condition: Condition) => boolean
    parseFromDTO: (condition: ConditionDTO) => Condition
    parseToDTO: (id: string, condition: Condition) => ConditionDTO | null
}

const parseFromDTOParsers = {
    [TextConditionParser.DefinitionDTOType]: TextConditionParser.parseFromDTO,
    [DecimalConditionParser.DefinitionDTOType]:
        DecimalConditionParser.parseFromDTO,
    [BooleanConditionParser.DefinitionDTOType]:
        BooleanConditionParser.parseFromDTO,
    [OptionFieldDefinitionType]: SelectorConditionParser.parseFromDTO,
    [OptionsFieldDefinitionType]: SelectorConditionParser.parseFromDTO,
    [AccountCodeFieldDefinitionType]: SelectorConditionParser.parseFromDTO,
    [ActivityFieldDefinitionType]: SelectorConditionParser.parseFromDTO,
    [ApplicationFieldDefinitionType]: SelectorConditionParser.parseFromDTO,
    [ApplicationVersionFieldDefinitionType]:
        SelectorConditionParser.parseFromDTO,
    [BillTypeFieldDefinitionType]: SelectorConditionParser.parseFromDTO,
    [DisplayTypeFieldDefinitionType]: SelectorConditionParser.parseFromDTO,
    [ImpactFieldDefinitionType]: SelectorConditionParser.parseFromDTO,
    [ProbabilityFieldDefinitionType]: SelectorConditionParser.parseFromDTO,
    [UserGroupFieldDefinitionType]: SelectorConditionParser.parseFromDTO,
    [UserGroupsFieldDefinitionType]: SelectorConditionParser.parseFromDTO,
    [UserFieldDefinitionType]: SelectorConditionParser.parseFromDTO,
    [UsersFieldDefinitionType]: SelectorConditionParser.parseFromDTO,
    [TagsFieldDefinitionType]: SelectorConditionParser.parseFromDTO,
}

const parseToDTOParsers = {
    [TextConditionParser.Type]: TextConditionParser.parseToDTO,
    [DecimalConditionParser.Type]: DecimalConditionParser.parseToDTO,
    [BooleanConditionParser.Type]: BooleanConditionParser.parseToDTO,
    [SelectorConditionParser.Type]: SelectorConditionParser.parseToDTO,
}

export const parseFromDTO = (dto: FiltersDTO): Filters => {
    const parsedFilters = dto.Conditions.reduce((acc, condition) => {
        const fieldDefType = condition.FieldDefinition?.["@type"]

        if (!fieldDefType) {
            return acc
        }

        const parser = parseFromDTOParsers[fieldDefType]

        if (!parser) {
            console.error("Field definition type not valid")
            return acc
        }

        const parsed = parser(condition)

        if (parsed && condition?.FieldDefinition?.Id) {
            return { ...acc, [condition.FieldDefinition.Id]: parsed }
        }
        return acc
    }, {} as Filters)

    return parsedFilters as Filters
}

export const parseToDTO = (filters: Filters): FiltersDTO => {
    return {
        "@type": "#TF.LogicalCondition",
        Operator: "And",
        Conditions: Object.entries(filters)
            .map((entry) => {
                const [id, condition] = entry
                if (!condition) {
                    return
                }
                return (
                    parseToDTOParsers[condition.type]?.(id, condition) ?? null
                )
            })
            .filter((condition) => condition !== null) as ConditionDTO[],
    }
}
